import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The fractions strand — the spine of the MVP.
 *
 * These eight lessons are written to be *read*, not skimmed. Each one names the
 * misconception it is trying to prevent, because the exercises attached to the
 * same concept are built to catch exactly that misconception.
 */
export const FRACTION_LESSONS: Lesson[] = [
  lesson({
    id: 'fraction-basics-01',
    unitId: 'unit-fractions-meaning',
    conceptId: 'fraction-basics',
    title: 'What a Fraction Is',
    summary: 'A fraction names equal parts of a whole. The bottom number sets the size of the parts; the top number counts them.',
    objectives: [
      'Read and write fractions for shaded regions',
      'Explain what the numerator and denominator each mean',
      'Recognise that the parts must be equal',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'One number, two jobs',
        blocks: [
          text(
            'You already know how to count whole things: three apples, seven metres, twelve minutes. But most of the world does not arrive in whole pieces. Half a sandwich. Three quarters of an hour. Two thirds of the class.',
          ),
          text(
            'A fraction is how mathematics writes an amount that is smaller than one whole — or made of wholes and a bit more. It does that with two numbers doing two different jobs.',
          ),
          math('\\frac{3}{4}', 'Read aloud: "three quarters".'),
        ],
      },
      intuition: {
        title: 'The bottom number names the pieces',
        blocks: [
          text(
            'Imagine a chocolate bar. Break it into 4 equal pieces and each piece is called a quarter. Break the same bar into 8 equal pieces instead and each piece is called an eighth.',
          ),
          text(
            'The bottom number — the **denominator** — tells you what the pieces are called, which is another way of saying how big they are. The top number — the **numerator** — simply counts how many of those pieces you have.',
          ),
          insight(
            'The denominator is not counted. It is a name, like "metre" or "minute". When you write 3/4 you are saying "three quarters" in exactly the way you would say "three metres".',
            'The idea that makes everything else work',
          ),
          warning(
            'The pieces must be equal. Breaking a bar into one huge piece and three tiny ones does not give you quarters — it gives you a mess.',
          ),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'fraction-bar', parts: 4, shaded: 3, label: '3/4' }, 'Three of the four equal parts are shaded.'),
          text('The same amount can be shown as a share of a circle, a length on a number line, or a group of objects. The picture changes; the fraction does not.'),
          figure(
            { kind: 'number-line', from: 0, to: 1, step: 0.25, marks: [{ at: 0.75, label: '3/4' }] },
            'A fraction is also a *position*: three quarters of the way from 0 to 1.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A pizza is cut into 6 equal slices. You eat 2 of them. What fraction of the pizza did you eat?'),
          steps([
            { label: 'How many equal pieces is the whole cut into?', latex: '6', note: 'That is the denominator — the pieces are called sixths.' },
            { label: 'How many of those pieces do you have?', latex: '2', note: 'That is the numerator.' },
            { label: 'Write it as a fraction.', latex: '\\frac{2}{6}' },
          ]),
          figure({ kind: 'fraction-bar', parts: 6, shaded: 2 }),
          tip('Say the answer out loud: "two sixths". If it sounds like counting, you have understood it.'),
        ],
      },
      guided: {
        title: 'Try these with support',
        blocks: [
          text('Each of these gives you a picture. Name the fraction it shows. If you get one wrong, look at the picture again before reading the explanation — the answer is usually visible.'),
          practice('fraction-basics', 4, 'Name the fraction shown.'),
        ],
      },
      independent: {
        title: 'On your own',
        blocks: [practice('fraction-basics', 5, 'Read and write fractions without a picture to lean on.')],
      },
      challenge: {
        title: 'Something harder',
        blocks: [
          text('These questions ask you to reason about fractions rather than just read them off a diagram.'),
          practice('fraction-basics', 3),
        ],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The denominator (bottom) names the size of the pieces.',
            'The numerator (top) counts how many pieces you have.',
            'The pieces must be equal, or the fraction means nothing.',
            'A fraction is also a position on the number line.',
          ]),
          insight('Everything else about fractions — comparing, adding, multiplying — follows from those two jobs. When a fraction problem confuses you, go back to "how big are the pieces, and how many do I have?"'),
        ],
      },
    },
  }),

  lesson({
    id: 'equivalent-fractions-01',
    unitId: 'unit-fractions-equivalence',
    conceptId: 'equivalent-fractions',
    title: 'Equivalent Fractions',
    summary: 'Different fractions can name the same amount. Cutting every piece into the same number of smaller pieces changes the name, not the amount.',
    objectives: [
      'Generate equivalent fractions by multiplying or dividing top and bottom',
      'Explain why multiplying by 2/2 does not change the value',
      'Recognise equivalent fractions from a diagram',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Two names, one amount',
        blocks: [
          text('Half a pizza. Two quarters of a pizza. Four eighths of a pizza. Put them side by side and they are the same amount of pizza — but they have three different names.'),
          math('\\frac{1}{2} = \\frac{2}{4} = \\frac{4}{8}'),
          text('Fractions that name the same amount are called **equivalent**. This is not a coincidence or a trick; it happens for a reason you can see.'),
        ],
      },
      intuition: {
        title: 'Why it happens',
        blocks: [
          text('Take half a bar. Now cut every piece in half again. You have not eaten anything and you have not added anything — but where there was 1 piece out of 2, there are now 2 pieces out of 4.'),
          text('Cutting doubled the number of pieces you have *and* the number of pieces in the whole. Both numbers doubled, so the amount stayed the same.'),
          math('\\frac{1}{2} = \\frac{1 \\times 2}{2 \\times 2} = \\frac{2}{4}'),
          insight(
            'Multiplying the top and the bottom by the same number is multiplying by 2/2, or 3/3, or 5/5 — and each of those is just another name for 1. Multiplying by 1 never changes a number.',
            'The real reason',
          ),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 2, shaded: 1, label: '1/2' },
                { parts: 4, shaded: 2, label: '2/4' },
                { parts: 8, shaded: 4, label: '4/8' },
              ],
            },
            'Same shaded length, three different ways of naming it.',
          ),
          text('Notice that the shaded region does not move. Only the cutting lines change.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write a fraction equivalent to 2/3 with a denominator of 12.'),
          steps([
            { label: 'What do you multiply 3 by to get 12?', latex: '3 \\times 4 = 12', note: 'The pieces are being cut into 4 smaller pieces each.' },
            { label: 'Do the same to the numerator.', latex: '2 \\times 4 = 8', note: 'Whatever you do to the bottom, do to the top.' },
            { label: 'Write the equivalent fraction.', latex: '\\frac{2}{3} = \\frac{8}{12}' },
          ]),
          table(['Fraction', 'Multiply by', 'Result'], [
            ['2/3', '2/2', '4/6'],
            ['2/3', '3/3', '6/9'],
            ['2/3', '4/4', '8/12'],
          ]),
        ],
      },
      guided: {
        title: 'Try these with support',
        blocks: [practice('equivalent-fractions', 4, 'Fill in the missing number so the fractions are equivalent.')],
      },
      independent: {
        title: 'On your own',
        blocks: [practice('equivalent-fractions', 5)],
      },
      challenge: {
        title: 'Something harder',
        blocks: [
          text('Going the other way — dividing top and bottom — is just as valid, and it is what simplifying will turn out to be.'),
          practice('equivalent-fractions', 3),
        ],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Multiply the top and bottom by the same number: the value does not change.',
            'Divide the top and bottom by the same number: the value does not change.',
            'Adding the same number to top and bottom does NOT work — 1/2 is not 2/3.',
          ]),
          warning('That last point catches people out. Multiplication scales both parts of the fraction in proportion; addition does not.'),
        ],
      },
    },
  }),

  lesson({
    id: 'simplify-fractions-01',
    unitId: 'unit-fractions-equivalence',
    conceptId: 'simplify-fractions',
    title: 'Simplifying Fractions',
    summary: 'Of all the names a fraction has, one is simplest: the one with the largest possible pieces.',
    objectives: [
      'Divide the numerator and denominator by a common factor',
      'Find the greatest common factor to simplify in one step',
      'Recognise when a fraction is already in lowest terms',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Choosing the best name',
        blocks: [
          text('You now know that 6/12, 3/6 and 1/2 are the same amount. When you write an answer, you write the last one. Why?'),
          text('Because 1/2 uses the largest pieces. It is the shortest true description of the amount — and short, true descriptions are what mathematicians are after.'),
          math('\\frac{6}{12} = \\frac{1}{2}', 'The same amount, in lowest terms.'),
        ],
      },
      intuition: {
        title: 'Undoing the cutting',
        blocks: [
          text('Simplifying is equivalence run backwards. Instead of cutting every piece into smaller pieces, you glue pieces back together.'),
          text('6/12 means six pieces out of twelve. Glue them together in pairs: three pieces out of six. Glue them in pairs again: one piece and a half out of three — no. Go back and glue all six into one: one piece out of two.'),
          insight('A fraction is in **lowest terms** when the only number that divides both top and bottom is 1. At that point there is nothing left to glue.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 12, shaded: 6, label: '6/12' },
                { parts: 6, shaded: 3, label: '3/6' },
                { parts: 2, shaded: 1, label: '1/2' },
              ],
            },
            'Fewer, larger pieces each time — but the shaded length never changes.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Simplify 18/24.'),
          steps([
            { label: 'List the factors of each number.', latex: '18: 1, 2, 3, 6, 9, 18 \\qquad 24: 1, 2, 3, 4, 6, 8, 12, 24' },
            { label: 'Find the greatest one they share.', latex: '\\gcd(18, 24) = 6' },
            { label: 'Divide both by it.', latex: '\\frac{18 \\div 6}{24 \\div 6} = \\frac{3}{4}' },
            { label: 'Check nothing divides both 3 and 4 except 1.', latex: '\\frac{3}{4}', note: 'It is in lowest terms.' },
          ]),
          tip('If finding the greatest common factor is hard, divide by any common factor you spot and repeat. 18/24 → 9/12 → 3/4 gets you to the same place in two easy steps instead of one clever one.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('simplify-fractions', 4, 'Write each fraction in lowest terms.')] },
      independent: { title: 'On your own', blocks: [practice('simplify-fractions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('simplify-fractions', 3, 'Some of these are already simplified. Say so rather than changing them.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Divide the top and the bottom by a common factor.',
            'Dividing by the greatest common factor gets you there in one step.',
            'A fraction is in lowest terms when only 1 divides both numbers.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'compare-fractions-01',
    unitId: 'unit-fractions-compare',
    conceptId: 'compare-fractions',
    title: 'Comparing Fractions',
    summary: 'Which fraction is bigger? The answer depends on the size of the pieces, not on the size of the numbers.',
    objectives: [
      'Compare fractions with the same denominator',
      'Compare unit fractions and explain why a bigger denominator means a smaller fraction',
      'Compare fractions by rewriting them with a common denominator',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'The most common mistake in fractions',
        blocks: [
          text('Which is bigger: 1/3 or 1/8?'),
          text('Almost everybody, the first time, says 1/8 — because 8 is bigger than 3. Almost everybody is wrong.'),
          warning('A bigger denominator means the whole was cut into MORE pieces, so each piece is SMALLER. One eighth of a cake is less than one third of a cake.', 'Read this twice'),
        ],
      },
      intuition: {
        title: 'Share the cake with more people',
        blocks: [
          text('Imagine sharing one cake. Between 3 people, everyone gets a decent slice. Between 8 people, everyone gets a sliver. The more people share it, the less each person gets.'),
          text('The denominator is how many people are sharing.'),
          insight('Same numerator, bigger denominator → smaller fraction. Same denominator, bigger numerator → bigger fraction. Those two rules cover a surprising number of questions.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 3, shaded: 1, label: '1/3' },
                { parts: 8, shaded: 1, label: '1/8' },
              ],
            },
            'One piece each — but the pieces are not the same size.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Which is larger: 3/4 or 5/8?'),
          steps([
            { label: 'The pieces are different sizes, so make them the same.', latex: '4 \\text{ and } 8 \\rightarrow 8', note: 'Eighths will do: 8 is a multiple of 4.' },
            { label: 'Rewrite 3/4 in eighths.', latex: '\\frac{3}{4} = \\frac{3 \\times 2}{4 \\times 2} = \\frac{6}{8}' },
            { label: 'Now the pieces match, so just count them.', latex: '\\frac{6}{8} > \\frac{5}{8}' },
            { label: 'Answer.', latex: '\\frac{3}{4} > \\frac{5}{8}' },
          ]),
          tip('Another route: both fractions are close to 1. 3/4 is 1/4 short; 5/8 is 3/8 short. Since 1/4 = 2/8 is less than 3/8, the first one is closer to 1 — and therefore larger.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('compare-fractions', 4, 'Choose the larger fraction.')] },
      independent: { title: 'On your own', blocks: [practice('compare-fractions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('compare-fractions', 3, 'Order these fractions from smallest to largest.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Same denominator: compare the numerators.',
            'Same numerator: the bigger denominator is the SMALLER fraction.',
            'Neither the same: rewrite with a common denominator, then compare.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'common-denominators-01',
    unitId: 'unit-fractions-compare',
    conceptId: 'common-denominators',
    title: 'Common Denominators',
    summary: 'Before fractions can be compared or combined, their pieces have to be the same size.',
    objectives: [
      'Find a common denominator for two fractions',
      'Find the least common denominator',
      'Rewrite a pair of fractions with a common denominator',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'You cannot count pieces of different sizes',
        blocks: [
          text('If I give you 2 metres and 3 centimetres, you cannot say "5" of anything. The units do not match, so the numbers cannot be combined until you fix that.'),
          text('Fractions work exactly the same way. Thirds and quarters are different units. Before you can compare or add them, they have to be rewritten in one shared unit — a **common denominator**.'),
        ],
      },
      intuition: {
        title: 'Finding a shared unit',
        blocks: [
          text('Thirds and quarters do not match. But cut every third into 4 and every quarter into 3, and both become twelfths.'),
          math('\\frac{1}{3} = \\frac{4}{12} \\qquad \\frac{1}{4} = \\frac{3}{12}'),
          insight('12 works because it is a multiple of both 3 and 4. Any common multiple works; the smallest one keeps the numbers small, which is why it is called the *least* common denominator.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 3, shaded: 1, label: '1/3' },
                { parts: 12, shaded: 4, label: '4/12' },
                { parts: 4, shaded: 1, label: '1/4' },
                { parts: 12, shaded: 3, label: '3/12' },
              ],
            },
            'Both fractions redrawn with twelfths — now the pieces line up.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Rewrite 5/6 and 3/8 with a common denominator.'),
          steps([
            { label: 'List multiples of each denominator.', latex: '6: 6, 12, 18, \\mathbf{24} \\qquad 8: 8, 16, \\mathbf{24}' },
            { label: 'The smallest shared multiple is the least common denominator.', latex: '\\operatorname{lcm}(6, 8) = 24' },
            { label: 'Rewrite the first fraction.', latex: '\\frac{5}{6} = \\frac{5 \\times 4}{6 \\times 4} = \\frac{20}{24}' },
            { label: 'Rewrite the second.', latex: '\\frac{3}{8} = \\frac{3 \\times 3}{8 \\times 3} = \\frac{9}{24}' },
          ]),
          tip('Multiplying the two denominators together always gives *a* common denominator — 6 × 8 = 48 would have worked here too. It is just not the smallest, so the numbers get bigger than they need to be.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('common-denominators', 4, 'Find the least common denominator.')] },
      independent: { title: 'On your own', blocks: [practice('common-denominators', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('common-denominators', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A common denominator is a common multiple of both denominators.',
            'The least common denominator keeps the arithmetic small.',
            'Rewriting a fraction with a new denominator never changes its value.',
          ]),
          insight('This lesson is the gateway to adding fractions. If adding ever goes wrong, come back here first.'),
        ],
      },
    },
  }),

  lesson({
    id: 'add-fractions-01',
    unitId: 'unit-fractions-arithmetic',
    conceptId: 'add-fractions',
    title: 'Adding and Subtracting Fractions',
    summary: 'Once the pieces are the same size, adding fractions is ordinary counting.',
    objectives: [
      'Add and subtract fractions with the same denominator',
      'Add and subtract fractions with different denominators',
      'Explain why the denominators are not added',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'The rule everyone gets wrong',
        blocks: [
          text('Here is the mistake this lesson exists to prevent:'),
          math('\\frac{1}{2} + \\frac{1}{3} \\ne \\frac{2}{5}', 'This is wrong — and it is wrong in an interesting way.'),
          text('Check it against reality. One half is already bigger than 2/5. Adding something to a half cannot possibly give you less than a half. The answer is not just wrong, it is impossible.'),
        ],
      },
      intuition: {
        title: 'Why denominators are never added',
        blocks: [
          text('Remember what the denominator is: the *name* of the pieces. When you add 3 metres and 2 metres you get 5 metres — you add the counts, and the unit stays "metres". You do not get 5 "square metres".'),
          math('3\\text{ fifths} + 1\\text{ fifth} = 4\\text{ fifths}'),
          math('\\frac{3}{5} + \\frac{1}{5} = \\frac{4}{5}'),
          insight('Add the numerators. Keep the denominator. That is the whole rule — provided the denominators already match.'),
          warning('If the denominators do not match, you cannot add yet. Go and make them match first; that is what common denominators are for.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 5, shaded: 3, label: '3/5' },
                { parts: 5, shaded: 1, label: '1/5' },
                { parts: 5, shaded: 4, label: '4/5' },
              ],
            },
            'Three fifths and one more fifth: four fifths. The piece size never changed.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Compute 2/3 + 1/4.'),
          steps([
            { label: 'The denominators differ, so find a common one.', latex: '\\operatorname{lcm}(3, 4) = 12' },
            { label: 'Rewrite both fractions in twelfths.', latex: '\\frac{2}{3} = \\frac{8}{12} \\qquad \\frac{1}{4} = \\frac{3}{12}' },
            { label: 'Now the pieces match. Add the numerators.', latex: '\\frac{8}{12} + \\frac{3}{12} = \\frac{11}{12}', note: 'The denominator stays 12.' },
            { label: 'Check it can be simplified — 11 and 12 share no factor.', latex: '\\frac{11}{12}' },
          ]),
          text('Subtraction is identical, except you subtract the numerators:'),
          math('\\frac{3}{4} - \\frac{1}{6} = \\frac{9}{12} - \\frac{2}{12} = \\frac{7}{12}'),
        ],
      },
      guided: {
        title: 'Try these with support',
        blocks: [
          text('These start with matching denominators, then move on to ones you have to fix first.'),
          practice('add-fractions', 5, 'Add or subtract. Simplify your answer.'),
        ],
      },
      independent: { title: 'On your own', blocks: [practice('add-fractions', 6)] },
      challenge: { title: 'Something harder', blocks: [practice('add-fractions', 3, 'Three fractions, or awkward denominators.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Same denominator: add or subtract the numerators, keep the denominator.',
            'Different denominators: rewrite with a common denominator first.',
            'Never add the denominators.',
            'Simplify the answer at the end.',
          ]),
          tip('Sanity-check every answer. Is it bigger than both fractions you started with (for addition)? Is it less than 1 when it should be? A five-second check catches most errors.'),
        ],
      },
    },
  }),

  lesson({
    id: 'multiply-fractions-01',
    unitId: 'unit-fractions-arithmetic',
    conceptId: 'multiply-fractions',
    title: 'Multiplying Fractions',
    summary: 'Multiplying by a fraction takes a part of something — which is why the answer can be smaller than what you started with.',
    objectives: [
      'Multiply two fractions',
      'Explain multiplication of fractions with an area model',
      'Recognise that multiplying by a fraction less than 1 makes a number smaller',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Multiplication does not always make things bigger',
        blocks: [
          text('You have spent years learning that multiplying makes numbers bigger. 6 × 4 is much more than 6.'),
          text('That stops being true the moment fractions arrive.'),
          math('\\frac{2}{3} \\times 6 = 4'),
          text('Two thirds *of* six is four. Four is smaller than six. Nothing has gone wrong — the word "times" has quietly changed meaning to "of".'),
        ],
      },
      intuition: {
        title: '"Times" means "of"',
        blocks: [
          text('Read 2/3 × 3/4 as "two thirds *of* three quarters". That sentence is something you can picture: take three quarters of a bar, then take two thirds of that.'),
          insight('Whenever a fraction multiplication confuses you, replace the × with the word "of" and read it aloud. The sentence usually tells you the answer.'),
        ],
      },
      visual: {
        title: 'The area model',
        blocks: [
          text('Start with a square. Shade three quarters of it going across. Then shade two thirds of it going down. The part shaded both ways is the answer.'),
          figure({ kind: 'area-model', rows: 3, columns: 4, shadedRows: 2, shadedColumns: 3 }, 'The overlap is 6 small rectangles out of 12 — one half.'),
          text('The grid has 3 × 4 = 12 small rectangles, and 2 × 3 = 6 of them are double-shaded. That is exactly where the multiplication rule comes from.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Compute 2/3 × 3/4.'),
          steps([
            { label: 'Multiply the numerators.', latex: '2 \\times 3 = 6' },
            { label: 'Multiply the denominators.', latex: '3 \\times 4 = 12' },
            { label: 'Write the product.', latex: '\\frac{2}{3} \\times \\frac{3}{4} = \\frac{2 \\times 3}{3 \\times 4} = \\frac{6}{12}' },
            { label: 'Simplify.', latex: '\\frac{6}{12} = \\frac{1}{2}' },
          ]),
          warning('No common denominator is needed. That is only for addition and subtraction. Multiplying goes straight across — which makes it easier than adding, not harder.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multiply-fractions', 4, 'Multiply. Simplify your answer.')] },
      independent: { title: 'On your own', blocks: [practice('multiply-fractions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('multiply-fractions', 3, 'Word problems, where you have to decide that multiplication is what is needed.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Multiply the numerators; multiply the denominators.',
            'No common denominator required.',
            '"Times" means "of" — 1/2 × 8 is half of 8.',
            'Multiplying by a fraction below 1 makes the result smaller.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'mixed-numbers-01',
    unitId: 'unit-fractions-arithmetic',
    conceptId: 'mixed-numbers',
    title: 'Mixed Numbers',
    summary: 'Wholes and parts written together — and how to convert between that and a single fraction.',
    objectives: [
      'Convert an improper fraction to a mixed number',
      'Convert a mixed number to an improper fraction',
      'Choose the more useful form for a given situation',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'More than a whole',
        blocks: [
          text('What if you have seven quarters of a pizza? That is more than one pizza — one whole pizza, and three quarters left over.'),
          math('\\frac{7}{4} = 1\\tfrac{3}{4}'),
          text('The left-hand form is called an **improper fraction**; the right-hand form is a **mixed number**. Neither is more correct. They are useful in different places.'),
        ],
      },
      intuition: {
        title: 'It is division',
        blocks: [
          text('Converting 7/4 to a mixed number is asking: how many whole pizzas can I make from 7 quarters? Four quarters make one pizza, so that is 7 ÷ 4 = 1 remainder 3.'),
          insight('The quotient is the whole number; the remainder is the new numerator; the denominator never changes. A fraction bar has always been a division sign.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 4, shaded: 4, label: 'one whole' },
                { parts: 4, shaded: 3, label: '3/4' },
              ],
            },
            'Seven quarters: one full bar and three quarters of another.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Convert 23/5 to a mixed number, then convert 3 2/7 to an improper fraction.'),
          steps([
            { label: 'Divide 23 by 5.', latex: '23 \\div 5 = 4 \\text{ remainder } 3' },
            { label: 'Quotient is the whole part, remainder is the numerator.', latex: '\\frac{23}{5} = 4\\tfrac{3}{5}' },
            { label: 'Going the other way: how many sevenths in 3 wholes?', latex: '3 \\times 7 = 21' },
            { label: 'Add the 2 sevenths already there.', latex: '21 + 2 = 23' },
            { label: 'Write it over the denominator.', latex: '3\\tfrac{2}{7} = \\frac{23}{7}' },
          ]),
          tip('Mixed numbers are easier to picture, so they are better for answers about real quantities. Improper fractions are easier to calculate with, so convert to them before multiplying.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('mixed-numbers', 4, 'Convert between the two forms.')] },
      independent: { title: 'On your own', blocks: [practice('mixed-numbers', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('mixed-numbers', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Improper → mixed: divide, and the remainder becomes the numerator.',
            'Mixed → improper: multiply the whole by the denominator, add the numerator.',
            'The denominator never changes in either direction.',
          ]),
        ],
      },
    },
  }),
];
